import { Component, ElementRef, Input, OnChanges, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ChannelSearchDialogComponent } from '@components/channel-search-dialog/channel-search-dialog.component';
import { ChannelService } from '@service/channel/channel.service';
import { Channel } from 'icreate/common/models/channel';
import { DigitalChannelTypes } from 'icreate/common/models/channel-config';
import { ChannelType, getDigitalTypesSet } from 'icreate/common/models/channel-type';
import { FlashingStatus } from 'icreate/common/models/editor-viewer-element';
import { lastValueFrom, take } from 'rxjs';

@Component({
    selector: 'app-button-flashing-state',
    templateUrl: './button-flashing-state.component.html',
    styleUrls: ['./button-flashing-state.component.css'],
    standalone: false,
})
export class ButtonFlashingStateComponent implements OnChanges {
    @Input() vesselId: string;
    @Input() flashingStatus: FlashingStatus;
    @Input() isDisabled: boolean;

    @ViewChild('colorPickerDiv') colorPickerDiv: ElementRef;

    flashingChannel: Channel;
    isLoadingChannel: boolean = false;

    flashingOptionIsOpen: boolean = false;

    startStatusOpen: boolean = false;
    terminateStatusOpen: boolean = false;

    statusOptions: string[];
    startStatus: string = undefined;
    terminateStatus: string = undefined;
    flashingColor: string = undefined;

    pickerTop: number;
    pickerLeft: number;

    allowedTypesSet: Set<ChannelType> = new Set(getDigitalTypesSet());

    constructor(
        private channelService: ChannelService,
        private dialog: MatDialog
    ) {}

    async ngOnChanges() {
        if (this.flashingStatus) {
            this.isLoadingChannel = true;
            const channel = await this.channelService.getByRowId(this.vesselId, this.flashingStatus.channelTag);
            if (channel) {
                this.flashingChannel = channel;
                this.flashingOptionIsOpen = this.flashingStatus.enabled;
                this.flashingColor = this.flashingStatus.color;
                const contactOn = channel.fcuData.digitalAlarmSetting.statusStringContactOn;
                const contactOff = channel.fcuData.digitalAlarmSetting.statusStringContactOff;
                this.startStatus = this.flashingStatus.start === 1 ? contactOn : contactOff;
                this.terminateStatus = this.flashingStatus.finish === 1 ? contactOn : contactOff;
                this.statusOptions = [contactOn, contactOff];
            }
            this.isLoadingChannel = false;
        }
    }

    async openChannelSelector() {
        const dialogRef = this.dialog.open(ChannelSearchDialogComponent, {
            data: {
                vesselId: this.vesselId,
                assignedChannel: this.flashingChannel,
                allowedTypesSet: DigitalChannelTypes,
                setShouldDisableTypes: true,
            },
        });

        const channelSelected: Channel = await lastValueFrom(dialogRef.afterClosed());
        if (channelSelected) {
            this.flashingChannel = undefined;
            try {
                this.isLoadingChannel = true;
                const channel = await this.channelService.getByFcuChannelId(
                    this.vesselId,
                    channelSelected.fcuData.fcuChannelId
                );
                this.isLoadingChannel = false;
                if (channel) {
                    this.flashingChannel = channel;
                    this.startStatus = undefined;
                    this.terminateStatus = undefined;
                    this.statusOptions = [
                        this.flashingChannel.fcuData.digitalAlarmSetting.statusStringContactOn,
                        this.flashingChannel.fcuData.digitalAlarmSetting.statusStringContactOff,
                    ];
                }
            } catch (err) {
                console.error(err);
            } finally {
                this.isLoadingChannel = false;
            }
        }
    }

    removeAssignedChannel() {
        this.flashingChannel = undefined;
        this.startStatus = undefined;
        this.terminateStatus = undefined;
    }

    updateToggle(event) {
        this.flashingOptionIsOpen = event.checked;
    }

    onStartStatusChange(value: string) {
        this.startStatus = value;
        this.terminateStatus = this.getOpposite(value);
    }

    onTerminateStatusChange(value: string) {
        this.terminateStatus = value;
        this.startStatus = this.getOpposite(value);
    }

    onPickerStateChange(state: boolean) {
        if (state) {
            const selectorPosition = this.colorPickerDiv.nativeElement.getBoundingClientRect();
            const scrollTop = window.scrollY;
            let top = selectorPosition.bottom + scrollTop;
            this.pickerTop = top - 35;
            this.pickerLeft = selectorPosition.left - 230 - 11;
        } else {
            this.pickerTop = null;
            this.pickerLeft = null;
        }
    }

    get currentFlashingStatus(): FlashingStatus {
        if (!this.flashingChannel || !this.flashingColor || !this.startStatus || !this.terminateStatus) {
            return null;
        }
        const contactOn = this.flashingChannel.fcuData.digitalAlarmSetting.statusStringContactOn;
        return {
            channelTag: this.flashingChannel?.rowId.toString(),
            color: this.flashingColor,
            start: this.startStatus === contactOn ? 1 : 0,
            finish: this.terminateStatus === contactOn ? 1 : 0,
            enabled: this.flashingOptionIsOpen,
        };
    }

    get hasMissingParameters() {
        if (this.flashingOptionIsOpen) {
            return !this.flashingChannel || !this.flashingColor || !this.startStatus || !this.terminateStatus;
        }
        return false;
    }

    private getOpposite(value: string): string {
        if (!this.statusOptions || this.statusOptions.length !== 2) return value;
        return this.statusOptions.find(option => option !== value) || value;
    }
}
